import {
  Box,
  Disclaimer,
  formatDateTimeShort,
  H5,
  lightTheme,
  LocationPrinter,
  P,
  Pill,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { Fragment } from 'react';
import {
  PRINTER_ERROR_LABEL,
  printerKindLabel,
} from '../helpers/printerLabels';
import { useLocationPrinters } from '../hooks/useLocationPrinters';
import { PrinterGrid, PrinterText } from './LocationPrinters.styled';

/**
 * The tablet stopped reporting on it (30 min, the backend's call). `stale`
 * is checked too only for a backend that does not send `inactive` yet.
 */
const isInactive = (printer: LocationPrinter) =>
  printer.inactive || printer.stale;

/**
 * What a printer's pill says. Inactive beats everything: a printer the tablet
 * no longer reports on is unknown, never "OK" or a red error on old news.
 */
const printerState = (printer: LocationPrinter) => {
  if (isInactive(printer)) {
    return { label: 'Inactive', color: lightTheme.lightGray, since: null };
  }
  if (printer.status === 'error') {
    return {
      label: printer.errorCode
        ? PRINTER_ERROR_LABEL[printer.errorCode] ?? printer.errorCode
        : 'Error',
      color: lightTheme.primary,
      since: printer.errorSince,
    };
  }
  return { label: 'OK', color: lightTheme.green, since: null };
};

/** "Tablet ...0718": the last four of the Android ID. */
const tabletLabel = (deviceId: string) => `Tablet ...${deviceId.slice(-4)}`;

/** The line under a printer's name. */
const printerDetails = (printer: LocationPrinter, showTablet: boolean) => {
  const inactive = isInactive(printer);
  return [
    printerKindLabel(printer.brand, printer.connection),
    showTablet && printer.deviceId && tabletLabel(printer.deviceId),
    !inactive && printer.status === 'error' && printer.message,
    printer.lastReportedAt &&
      (inactive
        ? `No report from the tablet since ${formatDateTimeShort(
            printer.lastReportedAt
          )}`
        : `last report ${formatDateTimeShort(printer.lastReportedAt)}`),
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The printers per tablet, in the server's order (errors first) inside each
 * group and by first appearance across them, so the tablet with a printer in
 * error leads. Printers of an older app (no deviceId) share one group.
 */
const groupByTablet = (printers: LocationPrinter[]) => {
  const groups = new Map<string | null, LocationPrinter[]>();
  printers.forEach((printer) => {
    const key = printer.deviceId ?? null;
    groups.set(key, [...(groups.get(key) ?? []), printer]);
  });
  return [...groups.entries()];
};

/** "since 14:00" today, "since Oct 15, 2:00 PM" before. */
const sinceLabel = (iso: string) => {
  const at = DateTime.fromISO(iso);
  return at.hasSame(DateTime.now(), 'day')
    ? `since ${at.toFormat('HH:mm')}`
    : `since ${formatDateTimeShort(iso)}`;
};

const PrinterRow = ({
  printer,
  showTablet,
}: {
  printer: LocationPrinter;
  showTablet: boolean;
}) => {
  const state = printerState(printer);
  return (
    <Box $flex $alignCenter $between $gap="16px">
      <PrinterText>
        <P $m="0" $bold>
          {printer.name || 'Unnamed printer'}
        </P>
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {printerDetails(printer, showTablet)}
        </Disclaimer>
      </PrinterText>

      <PrinterText $right>
        <Pill $background={state.color}>
          <Disclaimer $m="0" $color={lightTheme.white} $lineHeight="1">
            {state.label}
          </Disclaimer>
        </Pill>
        {state.since && (
          <Disclaimer $m="0" $color={lightTheme.primary}>
            {sinceLabel(state.since)}
          </Disclaimer>
        )}
      </PrinterText>
    </Box>
  );
};

/**
 * The location's tablet printers, as the tablet last reported them - the
 * place a printerError system problem points support to. Printers in error
 * come first (the server's order), grouped per tablet when there are more
 * than one. Printers unreported for 7 days are dropped by the server. Empty
 * until the tablet reports any: an older tablet app, or no printers.
 */
export const LocationPrinters = ({
  locationId,
}: {
  locationId: number | string;
}) => {
  const { printers, isLoading, isError } = useLocationPrinters(locationId);
  const groups = groupByTablet(printers);

  return (
    <>
      <H5 $mb="16">Printers</H5>

      {!isLoading && (isError || printers.length === 0) && (
        <P $m="0 0 40px" $color={lightTheme.lightGray}>
          {isError
            ? 'Could not load the printers.'
            : 'The tablet has not reported any printers yet.'}
        </P>
      )}

      {groups.map(([deviceId, group]) => (
        <Fragment key={deviceId ?? 'unknown'}>
          {/* One tablet: its id goes on each printer's line instead. */}
          {groups.length > 1 && (
            <P $m="0 0 8px" $bold $color={lightTheme.lightGray}>
              {deviceId ? tabletLabel(deviceId) : 'Tablet: unknown'}
            </P>
          )}
          <PrinterGrid>
            {group.map((printer) => (
              <PrinterRow
                key={printer.printerKey}
                printer={printer}
                showTablet={groups.length === 1}
              />
            ))}
          </PrinterGrid>
        </Fragment>
      ))}
    </>
  );
};
